// Container for login form.
import * as React from "react";
import { Box, CPText, Link } from "../../../shared/components/base";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./LoginPageStyles.scss";

import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  CardSubtitle,
  CardFooter,
  StackLayout,
} from "@progress/kendo-react-layout";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPFontAwesome from "shared/components/shared/CPFontAwesome/CPFontAwesome";
import CPEmailTextBox from "shared/components/shared/CPEmailTextBox/CPEmailTextBox";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";

import logo from "../../../assets/evans-bi-logo2.png";
import microsoftIcon from "@fortawesome/fontawesome-free/svgs/brands/microsoft.svg";
import { baseURL } from "shared/config";
import ErrorPage from "../ErrorPage/ErrorPage";
import {
  useRequestPasswordlessCodeMutation,
  useTestAccountLoginMutation,
} from "data/auth/auth";
import { storeCookie, checkAndUpdateRateLimit } from "shared/helpers";
import { accessTokenName } from "shared/config";
import { useSiteStatusQuery, isSiteStatusActive } from "data/app/app";
import dayjs from "dayjs";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get("error");
  const { data: siteStatus } = useSiteStatusQuery();

  const [showEmailForm, setShowEmailForm] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [emailError, setEmailError] = React.useState<string | null>(null);

  const isTestAccount = email.trim().toLowerCase().endsWith("@test.local");

const EMAIL_SIGNIN_RATE_LIMIT_KEY = "email_signin_submissions";
const EMAIL_SIGNIN_RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000;
const EMAIL_SIGNIN_MAX_SUBMISSIONS_PER_WINDOW = 5;

  const { mutate: requestCode, isLoading: isRequestingCode } =
    useRequestPasswordlessCodeMutation();

  const { mutate: testAccountLogin, isLoading: isTestAccountLoading } =
    useTestAccountLoginMutation();

  const handleEmailSignIn = () => {
    setEmailError(null);

    if (!email.trim()) {
      setEmailError("Please enter your email address.");
      return;
    }

    const rateLimit = checkAndUpdateRateLimit(
      EMAIL_SIGNIN_RATE_LIMIT_KEY,
      EMAIL_SIGNIN_MAX_SUBMISSIONS_PER_WINDOW,
      EMAIL_SIGNIN_RATE_LIMIT_WINDOW_MS,
    );
    if (!rateLimit.allowed) {
      const minutes = Math.ceil((rateLimit.retryAfterSeconds ?? 1) / 60);
      setEmailError(
        `Too many attempts. Please try again in ${minutes} minute${minutes > 1 ? "s" : ""}.`,
      );
      return;
    }

    if (isTestAccount) {
      if (!password.trim()) {
        setEmailError("Please enter your password.");
        return;
      }

      testAccountLogin(
        { email: email.trim(), password: password.trim() },
        {
          onSuccess: (data) => {
            if (data?.token) {
              const expires = data?.expires ?? dayjs().add(4, "hour").toDate().toUTCString();
              storeCookie(accessTokenName, data.token, expires);
            }
            window.location.href = "/";
          },
          onError: (err: unknown) => {
            const code = (err as Record<string, string>)?.error;
            if (code === "account_inactive") {
              setEmailError(
                "Your account has been deactivated. Please contact support.",
              );
            } else if (code === "invalid_password" || code === "no_password") {
              setEmailError("Incorrect password.");
            } else if (code === "no_account") {
              setEmailError("No account was found for that email.");
            } else {
              setEmailError("An error occurred. Please try again.");
            }
          },
        },
      );
      return;
    }

    requestCode(
      { email: email.trim() },
      {
        onSuccess: () => {
          navigate("/verify-code", { state: { email: email.trim() } });
        },
        onError: (err: unknown) => {
          const errorCode = (err as Record<string, string>)?.error;

          if (errorCode === "account_inactive") {
            setEmailError(
              "Your account has been deactivated. Please contact support.",
            );
          } else if (errorCode === "use_sso") {
            setEmailError(
              "This account uses Microsoft SSO. Please sign in using the 'Sign in with Microsoft' option.",
            );
          } else if (errorCode === "no_account") {
            navigate("/request-access", {
              state: { email: email.trim(), reason: "no_account" },
            });
          } else {
            setEmailError("An error occurred. Please try again.");
          }
        },
      },
    );
  };

  const isSubmitting = isRequestingCode || isTestAccountLoading;

  if (errorCode) {
    return <ErrorPage errorCode={errorCode} />;
  }

  return (
    <Card className="login-form-card">
      <CardHeader>
        <Box
          alignItems="center"
          justifyContent="center"
          className="logo-container"
        >
          <img
            src={logo}
            alt="BI Hub Logo"
            className="login-logo"
            width="300"
          />
        </Box>

        <CardTitle>
          {/* <CPText textStyle="h5" align="center">
            Sign In
          </CPText> */}
        </CardTitle>
        <CardSubtitle>
          <StackLayout gap={15} orientation="vertical">
            <CPText textStyle="subtitle" align="center">
              Sign in to access business intelligence reports and analytics for
              the Evans Network of Companies
            </CPText>
          </StackLayout>
        </CardSubtitle>
      </CardHeader>
      <CardBody>
        {isSiteStatusActive(siteStatus) ? (
          <StackLayout
            orientation="vertical"
            gap={16}
            align={{ horizontal: "center" }}
            style={{ padding: "12px 0" }}
          >
            <CPFontAwesome
              icon={"fa-solid fa-circle-info"}
              size={36}
              style={{ color: "#3a7bd5" }}
            />
            <CPText textStyle="h6" align="center">
              {siteStatus.header}
            </CPText>
            {siteStatus.body && (
              <CPText align="center" style={{ color: "#555" }}>
                {siteStatus.body}
              </CPText>
            )}
          </StackLayout>
        ) : !showEmailForm ? (
          <StackLayout orientation="vertical" gap={12}>
            <Box alignItems="center" flexDirection="column" spacing={10}>
              
              <CPButton
                themeColor="primary"
                style={{ width: "100%" }}
                onClick={() => setShowEmailForm(true)}
              >
                <CPFontAwesome icon={"fa-solid fa-envelope"} size={20} inline />
                Agent / Owner Operator / Driver - Sign In
              </CPButton>
              <CPButton
                className="evans-sso-button"
                themeColor="primary"
                style={{ width: "100%" }}
                onClick={() => {
                  window.location.href = `${baseURL}/api/bihub/auth/saml-login`;
                }}
              >
                <CPFontAwesome
                  icon={"fa-brands fa-microsoft"}
                  svgFallback={microsoftIcon}
                  size={20}
                  inline
                />
                Evans Corp - Sign In
              </CPButton>
            </Box>
          </StackLayout>
        ) : (
          <StackLayout orientation="vertical" gap={8}>
            <CPEmailTextBox
              name="passwordless-email"
              label="Email Address"
              value={email}
              onChange={(e) => {
                setEmail(e.value as string);
                setPassword("");
                setEmailError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isTestAccount) handleEmailSignIn();
              }}
            />
            {isTestAccount && (
              <CPTextBox
                name="test-account-password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.value as string);
                  setEmailError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleEmailSignIn();
                }}
              />
            )}
            {emailError && (
              <CPText color="error" fontSize="13px">
                {emailError}
              </CPText>
            )}
            <CPButton
              themeColor="primary"
              disabled={isSubmitting}
              onClick={handleEmailSignIn}
            >
              {isSubmitting
                ? isTestAccount
                  ? "Signing in..."
                  : "Sending code..."
                : isTestAccount
                  ? "Sign In"
                  : "Send Sign-In Code"}
            </CPButton>
            <Box alignItems="center" justifyContent="center">
              <CPButton
                fillMode="flat"
                onClick={() => {
                  setShowEmailForm(false);
                  setEmail("");
                  setPassword("");
                  setEmailError(null);
                }}
              >
                Back to sign-in options
              </CPButton>
            </Box>
          </StackLayout>
        )}
      </CardBody>
      {!isSiteStatusActive(siteStatus) && (
        <CardFooter>
          <StackLayout align={{ horizontal: "stretch" }}>
            <StackLayout
              orientation={"vertical"}
              align={{ horizontal: "start" }}
              gap={5}
            >
              <Link
                style={{ fontSize: "16px" }}
                as="button"
                title="Register for Access"
                onClick={() => navigate("/request-access")}
              />
              <Link
                style={{ fontSize: "16px" }}
                as="button"
                title="Need help logging in?"
                onClick={() => {
                  const supportBody = [
                    "Please answer the following questions to help us resolve your login issue quickly:",
                    "",
                    "1. What email address are you trying to log in with?",
                    "   [Enter your email here]",
                    "",
                    "2. What happens when you try to log in? (e.g. error message, no email received, etc.)",
                    "   [Describe what you see]",
                    "",
                    "3. Are you signing in with Microsoft or with Email?",
                    "   ",
                    "",
                    "4. Have you been able to log in before, or is this your first attempt?",
                    "   ",
                    "",
                    "5. Are there any error messages displayed? If so, please paste them below:",
                    "   [Paste any error messages here]",
                    "",
                    "6. Browser and device (e.g. Chrome on Windows 10):",
                    "   ",
                  ].join("\n");
                  window.location.href = `mailto:tech_deck@evansdelivery.com?subject=Evans%20BiHub%20-%20Login%20Help%20Requested&body=${encodeURIComponent(supportBody)}`;
                }}
              />
            </StackLayout>
          </StackLayout>
        </CardFooter>
      )}
    </Card>
  );
};

export default LoginPage;
